<template>
    <div v-if="mission">
        <!-- CTA (au-dessus) : toujours disponible si canToggle -->
        <div v-if="canToggle" class="d-flex justify-content-center mb-4 mt-2">
            <button v-if="!countdownActive" class="btn launch-cta" :disabled="launching" @click="launchTests">
                <span v-if="launching" class="spinner-border spinner-border-sm me-2"></span>
                Lancer les tests
                <span class="launch-cta-sub">5 minutes</span>
            </button>

            <div v-else class="countdown-panel">
                <span class="countdown-dot"></span>
                <div class="countdown-text">
                    <div class="countdown-title">Tests en cours</div>
                    <div class="countdown-time">{{ countdownLabel }}</div>
                </div>
            </div>
        </div>

        <!-- Titre : uniquement pendant le chrono -->
        <div v-if="countdownActive" class="d-flex justify-content-center align-items-center gap-2 flex-wrap mb-2">
            <h6 class="mb-0 text-center d-flex align-items-center gap-2">
                Tests à demander au chauffeur :
            </h6>
        </div>

        <!-- Layout compact en “capsules” flex-wrap -->
        <div v-if="hasAnyPrechecks" class="d-flex flex-wrap justify-content-center gap-3 prechecks-area"
            :class="{ 'locked-prechecks': isLocked }"
            :title="isLocked ? 'Lancez les tests pour activer la vérification automatique.' : ''">
            <div class="pc-block border border-secondary-subtle bg-white">
                <VehicleNameDisplay :balise="mission.transporteur"
                    class="bg-success bg-opacity-10 rounded px-2 py-1 me-2 mb-1" />
                <div class="d-flex flex-wrap gap-2">
                    <template v-for="v in transporteurValues" :key="v.id">
                        <span class="pc-pill" :class="v.value ? 'pc-ok' : 'pc-wait'">
                            <span class="pc-dot" :class="v.value ? 'pc-dot-ok' : 'pc-dot-wait'"></span>
                            <span class="pc-label">{{ v.label }}</span>
                            <span class="pc-state">{{ v.value ? 'Validé' : 'En attente' }}</span>
                        </span>
                    </template>
                </div>
            </div>
            <div v-for="balise in balisesWithPrechecks" :key="balise.id"
                class="pc-block border border-secondary-subtle bg-white">
                <VehicleNameDisplay :balise="balise" class="bg-primary bg-opacity-10 rounded px-2 py-1 me-2 mb-1" />
                <div class="d-flex flex-wrap gap-2">
                    <template v-for="v in prechecksByBalise[balise.id]" :key="v.id">
                        <span class="pc-pill" :class="v.value ? 'pc-ok' : 'pc-wait'">
                            <span class="pc-dot" :class="v.value ? 'pc-dot-ok' : 'pc-dot-wait'"></span>
                            <span class="pc-label">{{ v.label }}</span>
                            <span class="pc-state">{{ v.value ? 'Validé' : 'En attente' }}</span>
                        </span>
                    </template>
                </div>
            </div>
        </div>
        <div v-else class="text-muted text-center">Aucun test requis pour ce convoi.</div>
    </div>
</template>

<script>
import VehicleNameDisplay from '@/components/display/VehicleNameDisplay.vue'
import MissionService from '@/services/mission.service'
import UserHelper from '@/helpers/user.helper.js'

export default {
    name: 'ConvoyPrechecks',
    props: { mission: { type: Object, required: true } },
    components: { VehicleNameDisplay },
    emits: ['refresh'],
    data() {
        return {
            launching: false,
            countdownEndAt: null,
            countdownTimer: null,
            nowTick: Date.now()
        };
    },
    computed: {
        // 🟩 ID du transporteur (depuis l'objet ou l'ID brut)
        transporteurId() {
            return this.mission?.transporteur?.id ?? this.mission?.transporteurId ?? null;
        },
        // Source unique : mission.missionPrecheckValues (refreshés par la page Mission)
        prechecksByBalise() {
            const vals = Array.isArray(this.mission?.missionPrecheckValues) ? this.mission.missionPrecheckValues : [];
            const byBalise = {};
            vals.forEach(v => {
                const bid = v.baliseId;
                if (!byBalise[bid]) byBalise[bid] = [];
                byBalise[bid].push(v);
            });
            return byBalise;
        },
        // ✅ Afficher le bloc si au moins un test existe (transporteur OU convoi)
        hasAnyPrechecks() {
            return (this.transporteurValues.length > 0) || (this.balisesWithPrechecks.length > 0);
        },
        // 🟩 Valeurs du transporteur (affichées au-dessus)
        transporteurValues() {
            if (!this.transporteurId) return [];
            return this.prechecksByBalise[this.transporteurId] || [];
        },
        balisesWithPrechecks() {
            const list = Array.isArray(this.mission?.balises) ? this.mission.balises : [];
            // 🟩 exclure le transporteur de la boucle convoi (puisqu'affiché au-dessus)
            return list
                .filter(b => b?.id !== this.transporteurId)
                .filter(b => (this.prechecksByBalise[b.id]?.length || 0) > 0);
        },
        canToggle() {
            const isViewer = UserHelper.isViewer(this.$store.state.auth.user);
            return !!this.mission && this.mission.status === 'PENDING' && !isViewer;
        },
        countdownActive() {
            return !!this.countdownEndAt && this.nowTick < this.countdownEndAt;
        },
        countdownLabel() {
            if (!this.countdownActive) return '';
            const ms = Math.max(0, this.countdownEndAt - this.nowTick);
            const totalSec = Math.ceil(ms / 1000);
            const m = Math.floor(totalSec / 60);
            const s = totalSec % 60;
            return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
        },
        countdownStorageKey() {
            const mid = this.mission?.id;
            return mid ? `mission_prechecks_countdown_${mid}` : null;
        },
        allPrechecksOk() {
            const vals = Array.isArray(this.mission?.missionPrecheckValues) ? this.mission.missionPrecheckValues : [];
            if (vals.length === 0) return true;
            return vals.every(v => v?.value === true);
        },
        // Zone grisée : uniquement si tests pas OK ET chrono pas en cours
        // ⚠️ Si tout est OK, le hover ne revient jamais (même chrono fini)
        isLocked() {
            return this.hasAnyPrechecks && !this.allPrechecksOk && !this.countdownActive;
        }
    },
    watch: {
        mission: {
            handler() {
                this.restoreCountdown();
            },
            deep: true
        }
    },
    mounted() {
        this.restoreCountdown();
        this.ensureCountdownTicking();
    },
    beforeUnmount() {
        if (this.countdownTimer) clearInterval(this.countdownTimer);
        this.countdownTimer = null;
    },
    methods: {
        async launchTests() {
            if (!this.mission?.id) return;
            if (!this.canToggle) return;
            if (this.countdownActive) return;

            try {
                this.launching = true;
                await MissionService.launchTest(this.mission.id);

                // 5 minutes
                const endAt = Date.now() + 5 * 60 * 1000;
                this.countdownEndAt = endAt;
                this.nowTick = Date.now();
                if (this.countdownStorageKey) {
                    localStorage.setItem(this.countdownStorageKey, String(endAt));
                }
                this.ensureCountdownTicking();
                this.$emit('refresh'); // pour récupérer mission.testLaunch si le parent refresh la mission
            } catch (e) {
                alert(e?.response?.data?.message || e.message || 'Erreur lors du lancement des tests.');
            } finally {
                this.launching = false;
            }
        },
        restoreCountdown() {
            if (!this.countdownStorageKey) return;
            const raw = localStorage.getItem(this.countdownStorageKey);
            const endAt = raw ? Number(raw) : null;
            if (endAt && Number.isFinite(endAt) && endAt > Date.now()) {
                this.countdownEndAt = endAt;
            } else {
                this.countdownEndAt = null;
                localStorage.removeItem(this.countdownStorageKey);
            }
        },
        ensureCountdownTicking() {
            if (this.countdownTimer) return;

            this.countdownTimer = setInterval(() => {
                this.nowTick = Date.now();

                if (!this.countdownActive) {
                    this.countdownEndAt = null;
                    if (this.countdownStorageKey) {
                        localStorage.removeItem(this.countdownStorageKey);
                    }
                    clearInterval(this.countdownTimer);
                    this.countdownTimer = null;
                }
            }, 1000);
        }
    }
}
</script>


<style scoped>
/* Zone “grisée + hover” tant que non activée */
.prechecks-area.locked-prechecks {
    position: relative;
    filter: grayscale(0.9);
    opacity: 0.22;
    transition: opacity .18s ease, filter .18s ease, transform .18s ease;
}

.prechecks-area.locked-prechecks:hover {
    opacity: 0.35;
    filter: grayscale(0.75);
    transform: translateY(-1px);
    cursor: not-allowed;
}

.prechecks-area.locked-prechecks::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: .6rem;
    background: rgba(255, 255, 255, 0.35);
    pointer-events: none;
}

/* CTA moderne, visible (sans dépendre de couleurs custom) */
.launch-cta {
    border: 1px solid rgba(0, 0, 0, .12);
    background: rgba(13, 110, 253, .08);
    padding: .55rem 1.1rem;
    border-radius: 999px;
    font-weight: 700;
    letter-spacing: .2px;
    box-shadow: 0 10px 22px rgba(0, 0, 0, .06);
    transition: transform .15s ease, box-shadow .15s ease, background .15s ease;
    display: inline-flex;
    align-items: center;
    gap: .6rem;
}

.launch-cta:hover {
    transform: translateY(-1px);
    box-shadow: 0 14px 26px rgba(0, 0, 0, .08);
    background: rgba(13, 110, 253, .12);
}

.launch-cta:active {
    transform: translateY(0px);
    box-shadow: 0 10px 22px rgba(0, 0, 0, .06);
}

.launch-cta:disabled {
    opacity: .6;
    transform: none;
}

.launch-cta-sub {
    font-size: .75rem;
    font-weight: 600;
    opacity: .7;
    padding-left: .1rem;
}

/* ===== Chrono / Tests en cours (plus visible) ===== */
.countdown-panel {
    display: inline-flex;
    align-items: center;
    gap: .75rem;
    padding: .6rem 1.1rem;
    border-radius: 999px;
    background: linear-gradient(135deg,
            rgba(255, 193, 7, 0.22),
            rgba(255, 193, 7, 0.12));
    border: 1px solid rgba(255, 193, 7, 0.55);
    box-shadow:
        0 6px 14px rgba(0, 0, 0, 0.10),
        inset 0 0 0 1px rgba(255, 255, 255, 0.35);
}

/* Point animé */
.countdown-dot {
    width: .7rem;
    height: .7rem;
    border-radius: 999px;
    background: #ffc107;
    box-shadow: 0 0 0 0 rgba(255, 193, 7, 0.6);
    animation: pulse-dot 1.2s infinite;
}

@keyframes pulse-dot {
    0% {
        box-shadow: 0 0 0 0 rgba(255, 193, 7, 0.6);
    }

    70% {
        box-shadow: 0 0 0 .55rem rgba(255, 193, 7, 0);
    }

    100% {
        box-shadow: 0 0 0 0 rgba(255, 193, 7, 0);
    }
}

.countdown-text {
    display: flex;
    flex-direction: column;
    line-height: 1.05;
}

.countdown-title {
    font-size: .72rem;
    font-weight: 700;
    letter-spacing: .05em;
    text-transform: uppercase;
    color: #856404;
}

.countdown-time {
    font-size: 1.1rem;
    font-weight: 800;
    color: #664d03;
}

/* Capsule contenant un véhicule + ses pré-checks, compacte et responsive */
.pc-block {
    display: inline-flex;
    flex-direction: column;
    gap: .35rem;
    padding: .5rem .6rem;
    border-radius: .6rem;
    max-width: 100%;
}

/* Pilules en lecture seule */
.pc-pill {
    display: inline-flex;
    align-items: center;
    gap: .45rem;
    font-size: .8rem;
    line-height: 1.1;
    padding: .15rem .5rem;
    border-radius: 999px;
    border: 1px solid;
    white-space: nowrap;
}

.pc-ok {
    color: #198754;
    border-color: rgba(25, 135, 84, .4);
    background: rgba(25, 135, 84, .08);
}

.pc-wait {
    color: #6c757d;
    border-color: rgba(108, 117, 125, .35);
    background: rgba(108, 117, 125, .08);
}

.pc-dot {
    width: .55rem;
    height: .55rem;
    border-radius: 999px;
    display: inline-block;
    box-shadow: 0 0 0 .15rem rgba(0, 0, 0, .03);
}

.pc-dot-ok {
    background: #198754;
}

.pc-dot-wait {
    background: #adb5bd;
}

.pc-label {
    font-weight: 600;
}

.pc-state {
    opacity: .85;
    font-size: .75rem;
    padding-left: .15rem;
}
</style>